import React, { useEffect, useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ResumeNudge } from "@/components/ResumeSessions";
import {
  LayoutDashboard, MessageSquareText, Swords, GraduationCap,
  Flame, Trophy, ShieldCheck, LogOut, Crown, Sparkles, Zap, Users, Route, UserCircle, CalendarDays
} from "lucide-react";

const nav = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/roleplay", label: "Roleplay", icon: Swords },
  { to: "/app/coach", label: "Coach", icon: MessageSquareText },
  { to: "/app/plan", label: "My Plan", icon: Route, id: "plan" },
  { to: "/app/courses", label: "Courses", icon: GraduationCap },
  { to: "/app/workshops", label: "Workshops", icon: CalendarDays },
  { to: "/app/challenges", label: "Challenges", icon: Flame },
  { to: "/app/battles", label: "Battles", icon: Zap },
  { to: "/app/tournaments", label: "Tournaments", icon: Trophy },
  { to: "/app/teams", label: "Teams", icon: Users },
  { to: "/app/leaderboard", label: "World Leaderboard", icon: Crown },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const canAdmin = isAdmin || !!user?.is_manager;
  const adminLabel = isAdmin ? "Admin" : "Manager";
  const [team, setTeam] = useState(null);

  useEffect(() => {
    if (user?.team_id) api.get("/teams/me").then((r) => setTeam(r.data.team)).catch((err) => console.warn("[Layout] request failed", err));
    else setTeam(null);
  }, [user?.team_id]);

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
      isActive
        ? "bg-emerald-500/15 text-emerald-600 border border-emerald-500/30"
        : "text-slate-500 hover:text-slate-900 hover:bg-slate-100 border border-transparent"
    }`;

  return (
    <div className="min-h-screen grid-bg" style={{ background: "#F4F7FB" }}>
      <ResumeNudge />
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 fixed left-0 top-0 bottom-0 z-40 bg-white border-r border-slate-200 p-4">
        <div className="flex items-center gap-2 px-2 py-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center glow-emerald">
            <Sparkles className="w-5 h-5 text-black" />
          </div>
          <div>
            <div className="font-display font-extrabold text-slate-900 leading-none">Pitch Craft</div>
            <div className="text-[10px] font-mono tracking-widest text-emerald-600/80 uppercase">Sales Coach</div>
          </div>
        </div>

        {team && (
          <div className="mt-2 flex items-center gap-2 px-2 py-2 rounded-xl bg-slate-50 border border-slate-200" data-testid="sidebar-team-brand">
            {team.logo_url
              ? <img src={team.logo_url} alt={team.name} className="w-7 h-7 rounded-md object-contain bg-white border border-slate-200" />
              : <div className="w-7 h-7 rounded-md bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-600 text-xs font-bold">{team.name?.[0]?.toUpperCase()}</div>}
            <div className="min-w-0">
              <div className="text-[9px] font-mono uppercase tracking-wider text-slate-400 leading-none">Team</div>
              <div className="text-xs font-semibold text-slate-700 truncate">{team.name}</div>
            </div>
          </div>
        )}

        <nav className="flex flex-col gap-1 mt-6">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={linkCls} data-testid={`nav-${n.id || n.label.toLowerCase()}`}>
              <n.icon className="w-[18px] h-[18px]" />
              {n.label}
            </NavLink>
          ))}
          {canAdmin && (
            <NavLink to="/app/admin" className={linkCls} data-testid="nav-admin">
              <ShieldCheck className="w-[18px] h-[18px]" />
              {adminLabel}
            </NavLink>
          )}
        </nav>

        <div className="mt-auto">
          <NavLink to="/app/pricing" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-amber-600 hover:bg-amber-500/10 transition-all" data-testid="nav-pricing">
            <Crown className="w-[18px] h-[18px]" /> Go Elite
          </NavLink>
          <div className="flex items-center gap-3 px-3 py-1 text-[11px] text-slate-400">
            <Link to="/privacy" className="hover:text-slate-600" data-testid="nav-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-slate-600" data-testid="nav-terms">Terms</Link>
          </div>
          <div className="mt-3 p-3 rounded-xl card-surface flex items-center gap-3">
            <NavLink to="/app/account" className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-black font-bold text-sm hover:ring-2 hover:ring-emerald-300 transition-all" title="Account settings" data-testid="nav-account-avatar">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </NavLink>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-900 truncate">{user?.name}</div>
              <NavLink to="/app/account" className="text-xs text-slate-500 hover:text-emerald-700" data-testid="nav-account">Level {user?.level || 1} · Account</NavLink>
            </div>
            <button onClick={() => { logout(); navigate("/"); }} className="text-slate-500 hover:text-red-600 transition-colors" data-testid="logout-btn">
              <LogOut className="w-[18px] h-[18px]" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="md:pl-64 pb-24 md:pb-0 min-h-screen">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">{children}</div>
      </main>

      {/* Mobile bottom nav — all tabs, horizontally scrollable */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur border-t border-slate-200 shadow-[0_-6px_20px_rgba(15,23,42,0.06)] md:hidden" data-testid="mobile-nav">
        <div className="flex items-stretch gap-1.5 px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] overflow-x-auto no-scrollbar snap-x">
          {[...nav.slice(0, 1).map((n) => ({ ...n, testid: n.id || n.label.toLowerCase() })),
            ...(canAdmin ? [{ to: "/app/admin", label: adminLabel, icon: ShieldCheck, testid: "admin", admin: true }] : []),
            ...nav.slice(1).map((n) => ({ ...n, testid: n.id || n.label.toLowerCase() })),
            { to: "/app/pricing", label: "Go Elite", icon: Crown, testid: "pricing", accent: true },
            { to: "/app/account", label: "Account", icon: UserCircle, testid: "account" },
          ].map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-2xl text-[11px] font-semibold shrink-0 min-w-[72px] snap-start transition-colors ${
                isActive
                  ? (n.accent ? "bg-amber-500 text-black shadow-sm" : "bg-emerald-500 text-black shadow-sm")
                  : (n.accent ? "text-amber-700 bg-amber-50" : n.admin ? "text-slate-900 bg-slate-100 border border-slate-300" : "text-slate-600 bg-slate-50 hover:bg-slate-100")}`}
              data-testid={`mobile-nav-${n.testid}`}>
              <n.icon className="w-6 h-6" />
              <span className="whitespace-nowrap leading-none">{n.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
